import type { Question, QuizFilter } from "../types/index.js";
import type { QuestionTracker } from "./QuestionTracker.js";

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export class QuizEngine {
  questions: Question[] = [];
  currentIndex = 0;
  score = 0;
  correctCount = 0;
  selectedAnswer: number | null = null;
  isFinished = false;
  sessionAnswerResults: Map<string, boolean> = new Map();
  private onChange: (() => void) | null = null;

  setOnChange(cb: () => void) {
    this.onChange = cb;
  }

  private notify() {
    this.onChange?.();
  }

  get currentQuestion(): Question | null {
    if (this.currentIndex >= this.questions.length) return null;
    return this.questions[this.currentIndex];
  }

  get progress(): number {
    if (this.questions.length === 0) return 0;
    return this.currentIndex / this.questions.length;
  }

  get progressLabel(): string {
    return `${this.currentIndex + 1} / ${this.questions.length}`;
  }

  get isAnswered(): boolean {
    return this.selectedAnswer !== null;
  }

  get isLastQuestion(): boolean {
    return this.currentIndex >= this.questions.length - 1;
  }

  get sessionWrongQuestions(): Question[] {
    return this.questions.filter(
      (q) => this.sessionAnswerResults.get(q.id) === false,
    );
  }

  loadQuestions(
    allQuestions: Question[],
    filter: QuizFilter = "all",
    unit?: string,
    subunit?: string,
    difficulty?: number,
    tracker?: QuestionTracker,
  ) {
    let filtered = shuffleArray(allQuestions);
    if (unit) filtered = filtered.filter((q) => q.unit === unit);
    if (subunit) filtered = filtered.filter((q) => q.subunit === subunit);
    if (difficulty)
      filtered = filtered.filter((q) => q.difficulty === difficulty);

    if (tracker) {
      switch (filter) {
        case "bookmarked":
          filtered = filtered.filter((q) =>
            tracker.bookmarkedIDs.has(q.id),
          );
          break;
        case "wrongOnly":
          filtered = filtered.filter((q) => tracker.wrongIDs.has(q.id));
          break;
        case "forgettingCurve": {
          const due = tracker.dueForReviewIDs;
          filtered = filtered.filter((q) => due.has(q.id));
          break;
        }
        case "all":
          break;
      }
    }

    this.questions = filtered;
    this.reset();
  }

  loadPresetQuestions(questions: Question[]) {
    this.questions = questions;
    this.reset();
  }

  selectAnswer(
    index: number,
    onRecordAnswer?: (correct: boolean) => void,
    tracker?: QuestionTracker,
  ) {
    if (this.selectedAnswer !== null) return;
    const q = this.currentQuestion;
    if (!q) return;

    this.selectedAnswer = index;
    const correct = index === q.answer;
    this.sessionAnswerResults.set(q.id, correct);

    if (correct) {
      this.score += 1;
      this.correctCount += 1;
      tracker?.clearWrongAnswer(q.id);
      tracker?.recordCorrectAnswer(q.id);
    } else {
      tracker?.recordWrongAnswer(q.id);
    }

    onRecordAnswer?.(correct);
    this.notify();
  }

  nextQuestion() {
    if (this.currentIndex >= this.questions.length - 1) {
      this.isFinished = true;
      this.notify();
      return;
    }
    this.currentIndex += 1;
    this.selectedAnswer = null;
    this.notify();
  }

  reset() {
    this.currentIndex = 0;
    this.score = 0;
    this.correctCount = 0;
    this.selectedAnswer = null;
    this.isFinished = false;
    this.sessionAnswerResults = new Map();
    this.notify();
  }
}
